import {
  blankPatientCase,
  ClinicModeContext,
  PatientCase,
  PatientCaseGrouping,
  setPatientCaseToLocalStore,
  useDiagnosticAgentState,
  useDiagnosticAgentType,
} from "../../context/diagnosticAgent";
import {
  CaseFindingPresentationType,
  Feature,
  MeasurementRange,
  MeasurementUnits,
  QualifiedFeature,
  QualifiedFeatureFragment,
  QualifiedFeatureQualifiers,
  QualifiedFeatureValue,
  Qualifier,
} from "fe-common/graphql";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useContext, useEffect, useMemo } from "react";
import {
  GenderValue,
  getSubPopulationFeatureID,
  KahunPanelGroup,
  Relations,
} from "shared";
import { sameDataInputQualifiedFeature } from "../dataInputTypes";
import { dedupQualifiers } from "fe-common/logic/qualifiedFeature/dedupQualifiers";
import { useDiagnosticNavigation } from "./useDiagnosticNavigation";
import { useUserStorage } from "fe-web-components/userStorage/useUserStorage";

// tbd - should come from grqphql
export enum PatientFindingPresentationType {
  ACTIVE_FINDING = "active_finding",
  BACKGROUND_FINDING = "background_finding",
}

export function caseGroupingToPresentationType(group: PatientCaseGrouping) {
  switch (group) {
    case PatientCaseGrouping.VITAL_SIGNS:
    case PatientCaseGrouping.REVIEW_OF_SYSTEMS:
    case PatientCaseGrouping.CLINICAL_FEATURES:
    case PatientCaseGrouping.WORKUP:
      return CaseFindingPresentationType.ActiveFinding;
    case PatientCaseGrouping.BACKGROUND:
      return CaseFindingPresentationType.BackgroundFinding;
    case PatientCaseGrouping.MAIN:
      return CaseFindingPresentationType.MainFinding;
    default:
      return CaseFindingPresentationType.ActiveFinding;
  }
}

export function panelGroupToCaseGrouping(panel: KahunPanelGroup | string) {
  switch (panel as KahunPanelGroup) {
    case KahunPanelGroup.CLINICAL_FEATURES:
      return PatientCaseGrouping.CLINICAL_FEATURES;
    case KahunPanelGroup.WORKUP:
      return PatientCaseGrouping.WORKUP;
    case KahunPanelGroup.BACKGROUND:
      return PatientCaseGrouping.BACKGROUND;
    case KahunPanelGroup.REVIEW_OF_SYSTEMS:
      return PatientCaseGrouping.REVIEW_OF_SYSTEMS;
  }
}

export function getPatientFindingsSection(
  section: PatientCaseGrouping,
  patient: PatientCase,
) {
  return patient[section] || [];
}

// TBD - improve this JM - we need findings to be stable unless actually changed.
// also linter is very wrong about what dependencies needed
/* eslint-disable react-hooks/exhaustive-deps */
function useAllFindings(patientCase: PatientCase) {
  return useMemo(() => {
    const ret = Object.values(PatientCaseGrouping).flatMap((s) =>
      getPatientFindingsSection(s, patientCase),
    );
    if (patientCase.age) ret.push(ageFeature(patientCase.age));
    if (patientCase.gender) ret.push(genderFeature(patientCase.gender));
    return ret;
  }, [
    patientCase.age,
    patientCase.gender,
    patientCase.workup,
    patientCase.background,
    patientCase.clinicalFeatures,
    patientCase.reviewOfSystems,
    patientCase.main,
  ]);
}
export enum PatientCaseOp {
  addToSection,
  deleteFromSection,
  demographic,
  chiefComplaint,
  updateQfInSection,
  addSkipToSection,
  replaceQFInSection,
}
export interface PatientCaseChange {
  op: PatientCaseOp;
  section: PatientCaseGrouping;
  qf?: QualifiedFeatureFragment;
  featureId?: string;
  gender?: GenderValue | null;
  age?: MeasurementRange | null;
}

/* eslint-enable react-hooks/exhaustive-deps */
//
// @param agentType - optional override. if null, agent is retrieved from context
//
export function usePatientCase() {
  // find our agentType
  let [agentType] = useDiagnosticAgentType();
  const clinicMode = useContext(ClinicModeContext);

  const userStorage = useUserStorage();

  const [
    { pendingFindings, patientCase, restoredCaseID, isLocalRestored },
    setState,
  ] = useDiagnosticAgentState();

  const { setEditPanelPopupProps } = useDiagnosticNavigation();

  useEffect(() => {
    // TODO: minimize calls to local storage
    if (!clinicMode)
      setPatientCaseToLocalStore(agentType, patientCase, userStorage);
  }, [agentType, patientCase, clinicMode, userStorage]);

  const setters = useMemo(() => {
    const setFieldToPatientCase = (
      field: keyof PatientCase,
      setter: (old: any) => any,
    ) => {
      setState((s) => ({
        ...s,
        dirty: true,
        restoredCaseChanged: true,
        patientCase: {
          ...s.patientCase,
          [field]: setter(s.patientCase[field]),
        },
      }));
    };

    const doChangesToPending = (changes: PatientCaseChange[]) => {
      doChangesToPatientCase(changes, true, true);
    };

    const doChangesToPatientCase = (
      changes: PatientCaseChange[],
      dirty: boolean = true,
      pending: boolean = false,
    ) => {
      // console.log(
      //   "CHANGES",
      //   changes.map((c) => [c.op, c.section, c.qf.feature.name]),
      // );
      const doChanges = (pc: PatientCase) => {
        const updated = { ...pc };
        changes.forEach(({ op, section, qf, featureId, age, gender }) => {
          // remove old
          if (
            op === PatientCaseOp.addToSection ||
            op === PatientCaseOp.deleteFromSection
          ) {
            const filter = qf
              ? (f) => !sameDataInputQualifiedFeature(f, qf)
              : (f) => f.feature.id !== featureId;

            const filteredQfs = updated[section]?.filter(filter);

            updated[section] = filteredQfs || [];
          }
          // do operation
          switch (op) {
            case PatientCaseOp.addSkipToSection:
            case PatientCaseOp.addToSection:
              updated[section].push(qf);
              break;
            case PatientCaseOp.demographic:
              if (age !== undefined) updated.age = age;
              if (gender !== undefined) updated.gender = gender;
              break;
            case PatientCaseOp.chiefComplaint:
              updated[PatientCaseGrouping.MAIN] = [qf]; //TODO: Change for Multiple CF
              break;
            case PatientCaseOp.replaceQFInSection: {
              let patientCaseQfIndex = updated[section].findIndex(
                (f) => sameDataInputQualifiedFeature(f, qf) && !f.skip,
              );
              if (patientCaseQfIndex > -1) {
                updated[section][patientCaseQfIndex] = qf;
              } else {
                updated[section].push(qf);
              }

              break;
            }
            case PatientCaseOp.updateQfInSection:
              let patientCaseQf = updated[section].find(
                (f) => sameDataInputQualifiedFeature(f, qf) && !f.skip,
              );
              if (!patientCaseQf) {
                //maybe its the chief complaint
                patientCaseQf = updated[PatientCaseGrouping.MAIN].find(
                  (f) => sameDataInputQualifiedFeature(f, qf) && !f.skip,
                );
              }
              if (!patientCaseQf) {
                updated[section].push(qf);
              } else {
                patientCaseQf.qualifiers = dedupQualifiers(
                  patientCaseQf.qualifiers.concat(qf.qualifiers),
                );
              }
              break;
          }
        });
        return updated;
      };

      const isDirty = (changes: PatientCaseChange[]): boolean =>
        dirty && changes.some((change) => !change.qf || !change.qf.skip);

      const dirtyToSet = isDirty(changes);

      pending
        ? setState((s) => ({
            ...s,
            pendingFindings: {
              ...s.pendingFindings,
              ...doChanges(s.pendingFindings),
            },
          }))
        : setState((s) => ({
            ...s,
            dirty: s.dirty || dirtyToSet,
            restoredCaseChanged: s.restoredCaseChanged || s.dirty || dirtyToSet,
            patientCase: {
              ...s.patientCase,
              ...doChanges(s.patientCase),
            },
          }));
    };

    const reset = () =>
      setState((prevState) => ({
        ...prevState,
        dirty: true,
        patientCase: blankPatientCase(),
        pendingFindings: blankPatientCase(),
      }));

    const setChips = (section: PatientCaseGrouping, setter) =>
      setFieldToPatientCase(section, (old) => setter(old || []));

    const handleChipRemove = (
      feature: QualifiedFeatureFragment,
      section: PatientCaseGrouping,
    ) => {
      if (feature) {
        setChips(section, (oldchips) => {
          return oldchips.filter(
            (f) => !sameDataInputQualifiedFeature(f, feature),
          );
        });
      }

      evt({
        category: CATEGORY.SELECTION,
        action: ACTION.REMOVE_FINDING_CHIP,
        label: feature.name,
      });
    };

    return {
      setPatientCase: (
        patientCase: PatientCase,
        restoredCaseID?: string,
        pending: boolean = false,
        restoredCaseChanged: boolean = false,
      ) => {
        pending
          ? setState((s) => ({
              ...s,
              dirty: true,
              pendingFindings: patientCase,
              restoredCaseID,
            }))
          : setState((s) => ({
              ...s,
              dirty: true,
              restoredCaseChanged,
              patientCase: patientCase,
              restoredCaseID,
            }));
      },

      isPendingChange: () => {
        let isPending = false;
        Object.values(PatientCaseGrouping).forEach((grouping) => {
          if (pendingFindings[grouping].length > 0) {
            isPending = true;
          }
        });
        return isPending;
      },

      clearPendingPlanChanges: () => {
        setState((prevState) => ({
          ...prevState,
          pendingFindings: blankPatientCase(),
        }));
      },

      applyPendingFindings: () => {
        Object.values(PatientCaseGrouping).forEach((grouping) => {
          const changes = pendingFindings[grouping].map((qf) => {
            return {
              op: PatientCaseOp.updateQfInSection,
              qf: qf,
              section: grouping,
            };
          });
          doChangesToPatientCase(changes);
        });

        setState((prevState) => ({
          ...prevState,
          dirty: true,
          restoredCaseChanged: true,
          pendingFindings: blankPatientCase(),
        }));

        setEditPanelPopupProps(null);
      },

      handleChipAddMultiple: (
        qfs: QualifiedFeatureFragment[],
        section = PatientCaseGrouping.CLINICAL_FEATURES,
      ) => {
        if (qfs) {
          qfs.forEach((qf) =>
            evt({
              category: CATEGORY.SELECTION,
              action: ACTION.ADD_FINDING_CHIP_DD,
              label: qf.name,
            }),
          );

          doChangesToPatientCase(
            qfs.map((qf) => ({
              op: PatientCaseOp.addToSection,
              qf,
              section,
            })),
          );
        }
      },

      handleChipRemove,

      handleChipRemoveMultiple: (
        qfs: QualifiedFeatureFragment[],
        section = PatientCaseGrouping.CLINICAL_FEATURES,
      ) => qfs?.forEach((qf) => handleChipRemove(qf, section)),

      handleSetGender: (g: GenderValue) => {
        evt({
          category: CATEGORY.AGENT_MAIN,
          action: ACTION.SELECT_GENDER,
          label:
            g === GenderValue.MALE
              ? "male"
              : g === GenderValue.FEMALE
              ? "female"
              : "unknonwn",
        });
        setFieldToPatientCase("gender", () => g);
      },

      handleSetAge: (age: number, units = MeasurementUnits.Years) => {
        evt({
          category: CATEGORY.AGENT_MAIN,
          action: ACTION.SELECT_AGE,
          label: (age || age === 0) && age.toString(),
        });
        setFieldToPatientCase("age", () => ({
          low: age,
          high: age,
          units: units,
        }));
      },

      handleReset: reset,

      handleSetChiefComplaint: (qf: QualifiedFeatureFragment) => {
        doChangesToPatientCase([
          {
            op: PatientCaseOp.chiefComplaint,
            qf,
            section: PatientCaseGrouping.MAIN,
          },
        ]);
      },

      handleRemoveChiefComplaint: (qf: QualifiedFeatureFragment) => {
        doChangesToPatientCase([
          {
            op: PatientCaseOp.deleteFromSection,
            qf,
            section: PatientCaseGrouping.MAIN,
          },
        ]);
      },

      handleDiseasesToAdd: (qf: QualifiedFeatureFragment) => {
        setState((s) => ({
          ...s,
          patientCase: {
            ...s.patientCase,
            diseasesToAdd: [...(s.patientCase.diseasesToAdd || []), qf],
          },
          dirty: true,
        }));
      },

      handlePanelsToAdd: (qf: QualifiedFeatureFragment) => {
        if (
          patientCase.panelsToAdd.find(
            (panel) =>
              panel.qualifiers[0].value.id === qf.qualifiers[0].value.id,
          )
        ) {
          return;
        }

        setState((s) => ({
          ...s,
          patientCase: {
            ...s.patientCase,
            panelsToAdd: [...(s.patientCase.panelsToAdd || []), qf],
          },
          dirty: false,
        }));
      },

      handleRemovePanel: (id: string) => {
        setState((s) => ({
          ...s,
          patientCase: {
            ...s.patientCase,
            panelsToAdd: s.patientCase.panelsToAdd.filter(
              (panel) => panel.qualifiers[0].value.id !== id,
            ),
          },
          dirty: true,
        }));
      },

      handleRemoveFromPlan: (qf: QualifiedFeatureFragment) => {
        evt({
          category: CATEGORY.AGENT_DD_LIST,
          action: ACTION.REMOVE_DISEASE_FROM_PLAN,
          label: qf.name,
        });

        const diseasesToIncludeInPlan = (state) =>
          state.patientCase.diseasesToIncludeInPlan?.filter(
            (dis) => dis?.id !== qf?.id,
          );

        const diseasesToRemoveFromPlan = (state) => [
          ...(state.patientCase.diseasesToRemoveFromPlan || []),
          qf,
        ];

        setState((s) => ({
          ...s,
          patientCase: {
            ...s.patientCase,
            diseasesToIncludeInPlan: diseasesToIncludeInPlan(s),
            diseasesToRemoveFromPlan: diseasesToRemoveFromPlan(s),
          },
          dirty: false,
          planDirty: true,
        }));
      },

      handleIncludeInPlan: (qf: QualifiedFeatureFragment) => {
        evt({
          category: CATEGORY.AGENT_DD_LIST,
          action: ACTION.ADD_DISEASE_TO_PLAN,
          label: qf.name,
        });

        const diseasesToRemoveFromPlan = (state) =>
          state.patientCase.diseasesToRemoveFromPlan?.filter(
            (dis) => dis?.id !== qf?.id,
          );

        const diseasesToIncludeInPlan = (state) => [
          ...(state.patientCase.diseasesToIncludeInPlan || []),
          qf,
        ];

        setState((s) => ({
          ...s,
          patientCase: {
            ...s.patientCase,
            diseasesToIncludeInPlan: diseasesToIncludeInPlan(s),
            diseasesToRemoveFromPlan: diseasesToRemoveFromPlan(s),
          },
          dirty: false,
          planDirty: true,
        }));
      },

      resetPlan: () => {
        evt({
          category: CATEGORY.AGENT_DD_LIST,
          action: ACTION.RESET_PLAN,
        });

        setState((s) => ({
          ...s,
          patientCase: {
            ...s.patientCase,
            diseasesToIncludeInPlan: [],
            diseasesToRemoveFromPlan: [],
            diseasesToAdd: [],
          },
          dirty: true,
        }));
      },

      setDirty: () => {
        setState((s) => ({
          ...s,
          dirty: true,
        }));
      },

      setPlanDirty: () => {
        setState((s) => ({
          ...s,
          planDirty: true,
        }));
      },

      doChangesToPatientCase,

      doChangesToPending,

      resetLocalRestored: () => {
        setState((s) => ({
          ...s,
          isLocalRestored: false,
        }));
      },

      agentType: agentType,
    };
  }, [
    agentType,
    setState,
    patientCase,
    pendingFindings,
    setEditPanelPopupProps,
  ]);

  const allFindings = useAllFindings(patientCase);

  return useMemo(
    () => ({
      patientCase,
      restoredCaseID,
      ...setters,
      allFindings,
      isLocalRestored,
      pendingFindings,
    }),
    [
      patientCase,
      restoredCaseID,
      setters,
      allFindings,
      isLocalRestored,
      pendingFindings,
    ],
  );
}

function makeSubPopulationFeature(
  relationId: Relations,
  valueId: string,
  range?: MeasurementRange,
): QualifiedFeature {
  const feature: Partial<Feature> = {
    id: getSubPopulationFeatureID(relationId),
  };
  const qualifier: Partial<QualifiedFeatureQualifiers> = {
    relation: {
      name: "",
      id: relationId,
      type: null,
      multi: false,
      essence: null,
    },
    range: range,
    value:
      valueId != null &&
      ({
        id: valueId,
      } as QualifiedFeatureValue),
  };
  return {
    id: relationId,
    feature,
    qualifiers: [qualifier],
    mandatory: qualifier,
  } as QualifiedFeature;
}

export function ageFeature(age: MeasurementRange): QualifiedFeature {
  const qf =
    age && makeSubPopulationFeature(Relations.PERIODS_OF_LIFE, null, age);
  if (qf) {
    qf.feature.name = "Age";
  }
  return qf;
}

export function genderFeature(gender: GenderValue): QualifiedFeature {
  const qf = gender && makeSubPopulationFeature(Relations.GENDER, gender);
  if (qf) {
    qf.feature.name = "Gender";
    const val = qf.qualifiers?.[0].value;
    if (val) {
      val.name =
        val.id === GenderValue.MALE
          ? "Male"
          : val.id === GenderValue.FEMALE
          ? "Female"
          : "Other";
    }
  }
  return qf;
}
